import { createContext, useEffect, useState } from "react";
import * as XLSX from "xlsx";

export const AnswersContext = createContext();

export default function AnswersProvider({ children }) {
  const [answerData, setAnswerData] = useState([]);

  // Load from localStorage on first render
  useEffect(() => {
    const stored = localStorage.getItem("answerData");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setAnswerData(parsed);
        }
      } catch {
        // ignore parse error
      }
    }
  }, []);

  const parseXlsxFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      const parsed = [];

      // Cerca il nome della colonna timestamp (possibili nomi)
      const timestampColumnNames = ['CreationTime', 'Date', 'Timestamp', 'InteractionDate', 'Data', 'Ora', 'Date/Time', 'DateTime'];
      let timestampColumnName = null;
      
      if (json.length > 0) {
        const firstRow = json[0];
        const keys = Object.keys(firstRow);
        
        // Prima cerca per nome esatto
        timestampColumnName = timestampColumnNames.find(name => firstRow.hasOwnProperty(name));
        
        // Se non trovato, cerca nella terza colonna (indice 2) - tipicamente dove si trova il timestamp
        if (!timestampColumnName && keys.length >= 3) {
          timestampColumnName = keys[2];
        }
        
        // Se ancora non trovato, cerca colonne che contengono valori che sembrano date/timestamp
        if (!timestampColumnName) {
          for (const key of keys) {
            if (key === 'Answers') continue; // Salta la colonna Answers
            const value = firstRow[key];
            // Gestisci sia stringhe che numeri (serial date di Excel)
            if (value !== null && value !== undefined && value !== '') {
              if (typeof value === 'string') {
                // Controlla se il valore sembra una data/timestamp
                const datePattern = /\d{1,2}\/\d{1,2}\/\d{2,4}/;
                if (datePattern.test(value)) {
                  timestampColumnName = key;
                  break;
                }
              } else if (typeof value === 'number' && value > 1000 && value < 1000000) {
                // Potrebbe essere un serial date di Excel (tipicamente tra ~1 e ~500000)
                timestampColumnName = key;
                break;
              }
            }
          }
        }
      }

      json.forEach((row) => {
        try {
          const parsedAnswer = JSON.parse(row.Answers);
          
          // Aggiungi il timestamp se disponibile
          let timestamp = null;
          if (timestampColumnName && row[timestampColumnName] !== null && row[timestampColumnName] !== undefined) {
            const rawTimestamp = row[timestampColumnName];
            
            // Se è un numero, potrebbe essere un serial date di Excel
            if (typeof rawTimestamp === 'number' && rawTimestamp > 1 && rawTimestamp < 1000000) {
              // Converti serial date di Excel a JavaScript Date
              const excelEpoch = new Date(1899, 11, 30); // 30 dicembre 1899
              const jsDate = new Date(excelEpoch.getTime() + (rawTimestamp - 1) * 86400 * 1000);
              // Salva come numero (millisecondi) per risparmiare spazio nel localStorage
              timestamp = jsDate.getTime();
            } else if (typeof rawTimestamp === 'string') {
              // Prova a parsare la stringa come data
              const parsedDate = new Date(rawTimestamp);
              if (!isNaN(parsedDate.getTime())) {
                timestamp = parsedDate.getTime();
              } else {
                // Se non è una data valida, salva come stringa originale
                timestamp = rawTimestamp;
              }
            } else if (rawTimestamp instanceof Date) {
              timestamp = rawTimestamp.getTime();
            } else if (typeof rawTimestamp === 'number' && rawTimestamp > 1000000000000) {
              // Già un timestamp Unix in millisecondi
              timestamp = rawTimestamp;
            }
          }
          
          // Aggiungi il timestamp all'oggetto delle risposte
          parsedAnswer._timestamp = timestamp;
          
          parsed.push(parsedAnswer);
        } catch (err) {
          // skip invalid rows
        }
      });

      setAnswerData(parsed);
      
      // Prova a salvare nel localStorage, ma gestisci l'errore di quota
      try {
        const dataString = JSON.stringify(parsed);
        localStorage.setItem("answerData", dataString);
      } catch (e) {
        // Se supera la quota, prova a salvare senza timestamp
        console.warn("localStorage quota exceeded, trying to save without timestamps");
        try {
          const dataWithoutTimestamps = parsed.map(({ _timestamp, ...rest }) => rest);
          localStorage.setItem("answerData", JSON.stringify(dataWithoutTimestamps));
        } catch (e2) {
          // Se ancora fallisce, prova a salvare solo i dati essenziali
          console.warn("Still too large, saving minimal data");
          try {
            const minimalData = parsed.map((entry) => {
              const { _timestamp, ...rest } = entry;
              // Rimuovi anche dati non essenziali se necessario
              return rest;
            });
            localStorage.setItem("answerData", JSON.stringify(minimalData));
          } catch (e3) {
            // Se tutto fallisce, non salvare ma continua comunque
            console.error("Failed to save to localStorage, data will only be in memory:", e3);
            // I dati sono comunque in memoria, l'app funziona lo stesso
          }
        }
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <AnswersContext.Provider value={{ answerData, parseXlsxFile }}>
      {children}
    </AnswersContext.Provider>
  );
}
